import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useLocation } from 'react-router';
import { CheckEmailMFA } from './components/check-email-MFA.component';
import { LoginFooter } from './components/login-footer.component';
import { LoginHeader } from './components/login-header.component';
import { LoginStepper } from './components/login-stepper.component';
import { UserContact } from '@uplift/types';
import { useFeatureFlags } from '@uplift/hooks';
import { Loader } from '@uplift/ui-v2';
import { MFAChallenge } from './components/mfa-challenge';

type MFAInfo = {
  contacts: UserContact[];
  email: string;
  userId: string;
  requestId: string;
};

export const LoginPage = () => {
  const location = useLocation();
  const { betterhelp_sso: isBetterhelpSsoEnabled, isLoading: isFlagsLoading } = useFeatureFlags([ 'betterhelp_sso' ]);
  const [ checkEmail, setCheckEmail ] = useState<string | null>(null);
  const [ mfaInfo, setMfaInfo ] = useState<MFAInfo | null>(null);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [ location.search ]);

  const loginHandler = React.useCallback((redirectUrl: string, code: string) => {
    window.location.replace(`${redirectUrl}?code=${code}`);
  }, []);

  const onCheckEmailHandler = (email: string) => {
    setCheckEmail(email);
  };

  const handleMfaChallenge = (userId: string, email: string, contacts: UserContact[], requestId: string) => {
    setMfaInfo({
      contacts,
      email,
      userId,
      requestId
    });
  };

  const handleMfaClose = () => {
    setMfaInfo(null);
  };

  const isAutoSignOut = !!searchParams.get('privacy_alert');

  if (checkEmail) {
    return <CheckEmailMFA email={checkEmail} />;
  }

  if (isFlagsLoading) {
    return <Loader />;
  }

  return (
    <>
      <Box
        sx={{
          flexGrow: 1,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 5,
          }}
        >
          <LoginHeader isAutoSignOut={isAutoSignOut} />
          <LoginStepper
            isBetterhelpSsoEnabled={!!isBetterhelpSsoEnabled}
            onSubmit={loginHandler}
            onCheckEmail={onCheckEmailHandler}
            onMfaChallenge={handleMfaChallenge}
          />
        </Box>
        <LoginFooter isAutoSignOut={isAutoSignOut} />
      </Box>
      {mfaInfo && (
        <MFAChallenge
          {...mfaInfo}
          onClose={handleMfaClose}
          onSuccess={loginHandler}
        />
      )}
    </>
  );
};
